import type { SupportedLanguage } from "@/lib/i18n/languages";

type KeyProofCopy = {
  keyPickupTitle: string;
  keyReturnTitle: string;
  keyPickupSubtitle: string;
  keyReturnSubtitle: string;
  add: string;
  remove: string;
  uploadFailed: string;
  removeFailed: string;
};

// Shipped literals keep the new evidence section translated even before a
// translations-table refresh. tDict still allows same-language admin overrides.
export const PROTOCOL_KEY_COPY: Record<SupportedLanguage, KeyProofCopy> = {
  en: {
    keyPickupTitle: "Key pickup proof",
    keyReturnTitle: "Key return proof",
    keyPickupSubtitle: "Add photo evidence of your key pickup.",
    keyReturnSubtitle: "Add photo evidence of your key return.",
    add: "Add photo",
    remove: "Remove photo",
    uploadFailed: "The photo could not be uploaded. Please try again.",
    removeFailed: "The photo could not be removed. Please try again.",
  },
  de: {
    keyPickupTitle: "Nachweis der Schlüsselabholung",
    keyReturnTitle: "Nachweis der Schlüsselrückgabe",
    keyPickupSubtitle:
      "Füge einen Fotonachweis deiner Schlüsselabholung hinzu.",
    keyReturnSubtitle:
      "Füge einen Fotonachweis deiner Schlüsselrückgabe hinzu.",
    add: "Foto hinzufügen",
    remove: "Foto entfernen",
    uploadFailed:
      "Das Foto konnte nicht hochgeladen werden. Bitte versuche es erneut.",
    removeFailed:
      "Das Foto konnte nicht entfernt werden. Bitte versuche es erneut.",
  },
  fr: {
    keyPickupTitle: "Preuve de récupération des clés",
    keyReturnTitle: "Preuve de restitution des clés",
    keyPickupSubtitle:
      "Ajoutez une photo attestant de la récupération de vos clés.",
    keyReturnSubtitle:
      "Ajoutez une photo attestant de la restitution de vos clés.",
    add: "Ajouter une photo",
    remove: "Supprimer la photo",
    uploadFailed: "La photo n’a pas pu être envoyée. Veuillez réessayer.",
    removeFailed: "La photo n’a pas pu être supprimée. Veuillez réessayer.",
  },
  ar: {
    keyPickupTitle: "إثبات استلام المفاتيح",
    keyReturnTitle: "إثبات إعادة المفاتيح",
    keyPickupSubtitle: "أضف صورة تثبت استلامك للمفاتيح.",
    keyReturnSubtitle: "أضف صورة تثبت إعادتك للمفاتيح.",
    add: "إضافة صورة",
    remove: "إزالة الصورة",
    uploadFailed: "تعذر تحميل الصورة. يرجى المحاولة مرة أخرى.",
    removeFailed: "تعذرت إزالة الصورة. يرجى المحاولة مرة أخرى.",
  },
  uk: {
    keyPickupTitle: "Підтвердження отримання ключів",
    keyReturnTitle: "Підтвердження повернення ключів",
    keyPickupSubtitle: "Додайте фото, що підтверджує отримання ключів.",
    keyReturnSubtitle: "Додайте фото, що підтверджує повернення ключів.",
    add: "Додати фото",
    remove: "Видалити фото",
    uploadFailed: "Не вдалося завантажити фото. Спробуйте ще раз.",
    removeFailed: "Не вдалося видалити фото. Спробуйте ще раз.",
  },
  es: {
    keyPickupTitle: "Justificante de recogida de llaves",
    keyReturnTitle: "Justificante de devolución de llaves",
    keyPickupSubtitle: "Añade una foto que acredite la recogida de tus llaves.",
    keyReturnSubtitle:
      "Añade una foto que acredite la devolución de tus llaves.",
    add: "Añadir foto",
    remove: "Eliminar foto",
    uploadFailed: "No se ha podido subir la foto. Inténtalo de nuevo.",
    removeFailed: "No se ha podido eliminar la foto. Inténtalo de nuevo.",
  },
};

type TranslateLiteral = (
  key: string,
  literal: string,
  language: SupportedLanguage
) => string;

export function getProtocolKeyCopy(
  language: SupportedLanguage,
  translate?: TranslateLiteral
): KeyProofCopy {
  const copy = { ...PROTOCOL_KEY_COPY[language] };
  if (translate) {
    for (const key of Object.keys(copy) as Array<keyof KeyProofCopy>) {
      copy[key] = translate(`protocol.keyProof.${key}`, copy[key], language);
    }
  }
  return copy;
}
